import { useEffect, useState } from 'react'
import {
  pushSupported, needsIosInstall, subscribeToPush,
  followTeam, unfollowTeam, fetchFollows,
} from '../lib/push'
import { track } from '../lib/events'

const STORAGE_KEY = 'followedTeamIds'

function readStored() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [] } catch { return [] }
}
function store(ids) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) } catch { /* private mode */ }
}

// status: 'unsupported' | 'needs-install' | 'idle' | 'following' | 'denied' | 'busy'
export default function useFollow(teamId, vapidPublicKey) {
  const [status, setStatus] = useState(() => {
    if (needsIosInstall()) return 'needs-install'
    if (!pushSupported()) return 'unsupported'
    return readStored().includes(teamId) ? 'following' : 'idle'
  })

  // Re-sync from the server: localStorage can be stale or wiped.
  useEffect(() => {
    if (needsIosInstall() || !pushSupported()) return
    let active = true
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => (sub ? fetchFollows(sub.endpoint) : null))
      .then((data) => {
        if (!active || !data) return
        store(data.teamIds)
        setStatus(data.teamIds.includes(teamId) ? 'following' : 'idle')
      })
      .catch(() => {})
    return () => { active = false }
  }, [teamId])

  async function follow() {
    setStatus('busy')
    try {
      const sub = await subscribeToPush(vapidPublicKey)
      if (!sub) {
        track('push-permission-denied', { teamId })
        return setStatus('denied')
      }
      const data = await followTeam(sub, teamId)
      store(data.teamIds)
      track('team-followed', { teamId })
      setStatus('following')
    } catch {
      track('push-subscribe-failed', { teamId })
      setStatus('idle')
    }
  }

  async function unfollow() {
    setStatus('busy')
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        const data = await unfollowTeam(sub.endpoint, teamId)
        store(data.teamIds)
      }
      track('team-unfollowed', { teamId })
      setStatus('idle')
    } catch {
      setStatus('following')
    }
  }

  return { status, follow, unfollow }
}
